import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AlertIcon, CurecallTag } from '@honestica/curecall-ui'
import { getDateValuesFromSchedule } from './programmed-message.helper'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { getDoctorName } from 'src/utils/string-formatter.util'
import { EAgeGroup, TMessageContentRule, TProgrammedMessageFormValues } from './programmed-message.type'
import { EHumanReviewRequestSubject } from 'src/pages/dashboard/_components/human-review-requests.type'
import { useLocale } from 'src/hooks/use-locale'
import { parseCombinedIsoDuration } from 'src/utils/duration-iso.util'

function ExclusionPill({ label }: { label: string }) {
  return (
    <span className="c-inline-flex c-items-center c-rounded-full c-border c-border-grey-400 c-bg-white c-px-3 c-py-1 c-font-nexa c-text-sm c-text-grey-700">
      {label}
    </span>
  )
}

export default function ProgrammedMessageRecap() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { watch } = useFormContext<TProgrammedMessageFormValues>()
  const { activeDoctors } = useCurrentProject()

  const schedule = watch('schedule')
  const humanReviewCheck = watch('humanReviewCheck')
  const durationDiff: string = schedule.durationDiff
  const atTime: string | undefined = schedule.atTime
  const avoidedWeekdays: number[] = schedule.avoidedWeekdays ?? []
  const avoidPublicHolidays: boolean = schedule.avoidPublicHolidays ?? false
  const avoidedDates: string[] = schedule.avoidedDates ?? []
  const preventRescheduleOnceTriggered: boolean = schedule.preventRescheduleOnceTriggered ?? false
  const gracePeriodDuration: string | null = schedule.gracePeriodDuration ?? null
  const profileRules: TMessageContentRule[] = watch('profileRules') ?? []

  const sendingScheduleLabel = (() => {
    if (!durationDiff) return null
    const { isBefore, value, multiplicator } = getDateValuesFromSchedule({ durationDiff })
    const dateLabel = t('pages.configuration.messages.programming-date-value', {
      value,
      multiplicator,
      isBefore,
    })
    if (!atTime) return dateLabel
    return t('pages.configuration.messages.recap-sending-with-hour', { dateLabel, hour: atTime })
  })()

  const weekdayLabelByDay: Record<number, string> = {
    1: t('pages.configuration.messages.weekday-mon'),
    2: t('pages.configuration.messages.weekday-tue'),
    3: t('pages.configuration.messages.weekday-wed'),
    4: t('pages.configuration.messages.weekday-thu'),
    5: t('pages.configuration.messages.weekday-fri'),
    6: t('pages.configuration.messages.weekday-sat'),
    7: t('pages.configuration.messages.weekday-sun'),
  }

  const exclusionPills: string[] = [
    ...[...avoidedWeekdays].sort((dayA, dayB) => dayA - dayB).map((day) => weekdayLabelByDay[day]),
    ...(avoidPublicHolidays ? [t('pages.configuration.messages.recap-public-holidays')] : []),
    ...avoidedDates.map((date) => {
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    }),
  ]

  const getDoctorLabel = (medicalStaffMemberId: string | null) => {
    if (!medicalStaffMemberId) return t('pages.configuration.messages.all-doctors')
    const doctor = activeDoctors.find((doc) => String(doc.id) === medicalStaffMemberId)
    return doctor ? getDoctorName(doctor) : t('pages.configuration.messages.all-doctors')
  }

  const getAgeLabel = (ageGroup: EAgeGroup | null) => {
    if (!ageGroup) return t('pages.configuration.messages.all-ages')
    return ageGroup === EAgeGroup.MINOR
      ? t('pages.configuration.messages.minor')
      : t('pages.configuration.messages.adult')
  }

  const hasRuleContent = (rule: TMessageContentRule) => !!rule.message?.[locale]

  const smsCheckLabel = (() => {
    if (!humanReviewCheck?.unreceivedSmsDelay || !humanReviewCheck.subject) return null
    try {
      const { days, hours, minutes } = parseCombinedIsoDuration(humanReviewCheck.unreceivedSmsDelay)
      const parts = [
        days > 0 && t('pages.configuration.messages.duration-days', { count: days }),
        hours > 0 && t('pages.configuration.messages.duration-hours', { count: hours }),
        minutes > 0 && t('pages.configuration.messages.duration-minutes', { count: minutes }),
      ].filter(Boolean) as string[]
      const duration = parts.join(' ')
      const tabLabelBySubject: Record<EHumanReviewRequestSubject, string> = {
        [EHumanReviewRequestSubject.UNKNOWN]: '',
        [EHumanReviewRequestSubject.CONVOCATION]: t('pages.dashboard.human-review-requests.tabs.convocation'),
        [EHumanReviewRequestSubject.POST_OP_CONTROL]: t(
          'pages.dashboard.human-review-requests.tabs.post-op-control',
        ),
        [EHumanReviewRequestSubject.ADMINISTRATIVE_DOCUMENTS]: t(
          'pages.dashboard.human-review-requests.tabs.administrative-documents',
        ),
        [EHumanReviewRequestSubject.MEDICAL_DOCUMENTS]: t(
          'pages.dashboard.human-review-requests.tabs.medical-documents',
        ),
      }
      return t('pages.configuration.messages.recap-sms-check', {
        duration,
        tab: tabLabelBySubject[humanReviewCheck.subject],
      })
    } catch {
      return null
    }
  })()

  const gracePeriodLabel = (() => {
    if (!gracePeriodDuration) return null
    try {
      const { days, hours, minutes } = parseCombinedIsoDuration(gracePeriodDuration)
      const parts = [
        days > 0 && t('pages.configuration.messages.duration-days', { count: days }),
        hours > 0 && t('pages.configuration.messages.duration-hours', { count: hours }),
        minutes > 0 && t('pages.configuration.messages.duration-minutes', { count: minutes }),
      ].filter(Boolean) as string[]
      return parts.length > 0 ? parts.join(' ') : null
    } catch {
      return null
    }
  })()

  return (
    <div className="c-rounded-xl c-border c-border-secondary-400 c-bg-secondary-100 c-px-5 c-py-5">
      <h3 className="c-m-0 c-mb-5 c-font-nexa c-text-base c-font-bold c-text-grey-900">
        {t('pages.configuration.messages.recap-title')}
      </h3>

      <div className="c-flex c-flex-col c-gap-4">
        <div className="c-flex c-items-center c-gap-2">
          <span className="c-text-base c-leading-none">⏰</span>
          <p className="c-m-0 c-font-nexa c-text-sm c-text-grey-600">
            <span className="c-font-semibold">{t('pages.configuration.messages.recap-sending')} : </span>
            {sendingScheduleLabel ?? t('pages.configuration.messages.recap-not-configured')}
          </p>
        </div>

        {exclusionPills.length > 0 && (
          <div className="c-flex c-flex-wrap c-items-center c-gap-x-2 c-gap-y-2">
            <span className="c-text-base c-leading-none">🚫</span>
            <p className="c-m-0 c-font-nexa c-text-sm c-text-grey-600">
              {t('pages.configuration.messages.recap-no-sending-on')} :
            </p>
            {exclusionPills.map((pill) => (
              <ExclusionPill key={pill} label={pill} />
            ))}
          </div>
        )}

        {preventRescheduleOnceTriggered && (
          <div className="c-flex c-items-center c-gap-2">
            <span className="c-text-base c-leading-none">🔒</span>
            <p className="c-m-0 c-font-nexa c-text-sm c-text-grey-600">
              {t('pages.configuration.messages.prevent-reschedule-label')}
            </p>
          </div>
        )}

        {smsCheckLabel && (
          <div className="c-flex c-items-center c-gap-2">
            <span className="c-text-base c-leading-none">📊</span>
            <p className="c-m-0 c-font-nexa c-text-sm c-text-grey-600">{smsCheckLabel}</p>
          </div>
        )}

        {gracePeriodLabel && (
          <div className="c-flex c-items-center c-gap-2">
            <span className="c-text-base c-leading-none">⏳</span>
            <p className="c-m-0 c-font-nexa c-text-sm c-text-grey-600">
              {t('pages.configuration.messages.recap-late-sending-allowed', { duration: gracePeriodLabel })}
            </p>
          </div>
        )}

        <div className="c-flex c-flex-col c-gap-2">
          <div className="c-flex c-items-center c-gap-2">
            <span className="c-text-base c-leading-none">📋</span>
            <p className="c-m-0 c-font-nexa c-text-sm c-text-grey-600">
              {t('pages.configuration.messages.recap-messages-count', { count: profileRules.length })}
            </p>
          </div>

          <div className="c-ml-7 c-flex c-flex-col c-gap-1.5">
            {profileRules.map((rule, index) => (
              <div key={index} className="c-flex c-items-center c-gap-2">
                {!hasRuleContent(rule) && (
                  <AlertIcon className="c-h-3.5 c-w-3.5 c-shrink-0 c-text-orange-700" />
                )}
                <span className="c-font-nexa c-text-sm c-text-grey-600">
                  {getDoctorLabel(rule.medicalStaffMemberId)} · {getAgeLabel(rule.ageGroup)}
                </span>
                {!hasRuleContent(rule) && (
                  <CurecallTag status="WARNING">
                    {t('pages.configuration.messages.recap-to-write')}
                  </CurecallTag>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
